import { Signal, computed, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Lux } from '@roosevelt/common-ui-lib/core';
import {
  errorFetchingAuthsToEdit,
  errorsInUpdatingAuths,
  getAdminAndUserAuthsResposne,
  getManageUserAuthsResp,
  isFetchingAuthsToEdit,
  updatingAuthInProgress
} from 'src/app/store/createManageUser/selector';
import {
  IAdminAndUserAuthResponse,
  ICreateManageUserState,
  IManageuser,
  ISubGroupAccessLevels,
  initialCreateManageUserState
} from 'src/app/store/createManageUser/state';
import { resolveManagedUserType, resolveScopedAccessLevelOptions, withFallbackUserAuthorizations } from './manage-auth-form.util';

export abstract class ManageAuthSharedBase {
  protected readonly getErrorsState: Signal<string>;
  protected readonly isFetchingAuthsToEditState: Signal<boolean>;
  protected readonly getUserAuthsToEditState: Signal<IAdminAndUserAuthResponse>;
  protected readonly errorsInUpdatingAuthsState: Signal<string>;
  protected readonly updatingAuthInProgressState: Signal<boolean>;
  protected readonly updatingAuthsDoneState: Signal<string>;
  protected readonly selectedOptionState = signal('');
  protected readonly updatingAuthsRespState = signal('');
  protected readonly showUpdateDialogState = signal(false);
  protected readonly userAndAdminAuthsState: Signal<IAdminAndUserAuthResponse>;
  protected readonly eligAccessLevelOptionsState: Signal<ReturnType<typeof resolveScopedAccessLevelOptions>>;
  protected readonly satAccessLevelOptionsState: Signal<ReturnType<typeof resolveScopedAccessLevelOptions>>;

  protected constructor(
    protected readonly lux: Lux<{ createManageUserState: ICreateManageUserState }>,
    protected readonly luxId: string
  ) {
    this.getErrorsState = toSignal(this.lux.get(errorFetchingAuthsToEdit, this.luxId), { initialValue: '' });
    this.isFetchingAuthsToEditState = toSignal(this.lux.get(isFetchingAuthsToEdit, this.luxId), { initialValue: false });
    this.getUserAuthsToEditState = toSignal(this.lux.get(getAdminAndUserAuthsResposne, this.luxId), {
      initialValue: initialCreateManageUserState.adminAndUserAuths.authResponse
    });
    this.errorsInUpdatingAuthsState = toSignal(this.lux.get(errorsInUpdatingAuths, this.luxId), { initialValue: '' });
    this.updatingAuthInProgressState = toSignal(this.lux.get(updatingAuthInProgress, this.luxId), { initialValue: false });
    this.updatingAuthsDoneState = toSignal(this.lux.get(getManageUserAuthsResp, this.luxId), { initialValue: '' });
    this.userAndAdminAuthsState = computed(() => withFallbackUserAuthorizations(
      this.getUserAuthsToEditState(),
      this.fallbackUserAuthorizations
    ));
    this.eligAccessLevelOptionsState = computed(() => resolveScopedAccessLevelOptions(
      this.userAndAdminAuthsState(),
      'eligibilityAccessLevel'
    ));
    this.satAccessLevelOptionsState = computed(() => resolveScopedAccessLevelOptions(
      this.userAndAdminAuthsState(),
      'satAccessLevel'
    ));
  }

  protected abstract get fallbackUserAuthorizations(): ISubGroupAccessLevels[];
  protected abstract get initialAdminUserAuthResponse(): IAdminAndUserAuthResponse;
  protected abstract get managedUserInfo(): IManageuser;
  protected abstract onManageUserAuthSuccess(selectedOption: string): void;

  get errors(): string {
    return this.getErrorsState();
  }

  get fetchingAuths(): boolean {
    return this.isFetchingAuthsToEditState();
  }

  get userAndAdminAuths(): IAdminAndUserAuthResponse {
    return this.userAndAdminAuthsState();
  }

  get selectedOption(): string {
    return this.selectedOptionState();
  }

  get updatingAuthsResp(): string {
    return this.updatingAuthsRespState();
  }

  set updatingAuthsResp(value: string) {
    this.updatingAuthsRespState.set(value);
  }

  get updatingAutherrors(): string {
    return this.errorsInUpdatingAuthsState();
  }

  get showUpdateDialog(): boolean {
    return this.showUpdateDialogState();
  }

  get eligAccessLevelOptions() {
    return this.eligAccessLevelOptionsState();
  }

  get satAccessLevelOptions() {
    return this.satAccessLevelOptionsState();
  }

  handleUpdateDialogVisibility(visible: boolean): void {
    if (!visible) {
      this.clearManageUserAuthFeedback();
    }
  }

  protected destroyManageUserState(): void {
    this.lux.destroy(this.luxId);
    this.resetSharedManageUserState();
  }

  protected clearAdminAndUserAuthState(): void {
    this.resetSharedManageUserState();
  }

  protected prepareManageUserAuthSubmission(): void {
    this.clearManageUserAuthFeedback();
    this.lux.set(state => state.createManageUserState.manageUserAuths, {
      authResponse: '',
      errors: ''
    });
  }

  protected clearManageUserAuthFeedback(): void {
    this.showUpdateDialogState.set(false);
    this.updatingAuthsRespState.set('');
  }

  protected completeManageUserAuthSubmission(): void {
    if (this.errorsInUpdatingAuthsState()) {
      return;
    }

    const response = this.updatingAuthsDoneState();
    if (!response) {
      return;
    }

    const selectedOption = untracked(() => this.selectedOptionState());
    const nextSelectedUserType = resolveManagedUserType(selectedOption);
    const currentUserInfo = untracked(() => this.managedUserInfo);
    this.updatingAuthsRespState.set(response);
    this.showUpdateDialogState.set(true);
    this.onManageUserAuthSuccess(selectedOption);

    if (currentUserInfo.selectedUserType !== nextSelectedUserType) {
      this.lux.set(state => state.createManageUserState.manageUser, {
        selectedUserType: nextSelectedUserType
      });
    }
  }

  private resetSharedManageUserState(): void {
    this.lux.set(state => state.createManageUserState.manageUserAuths, { authResponse: '', errors: '' });
    this.lux.set(state => state.createManageUserState.adminAndUserAuths, {
      authResponse: this.initialAdminUserAuthResponse,
      errors: ''
    });
  }
}
